import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

function formatDate(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "—";
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

/**
 * TimeRangeScroll
 *
 * Props:
 *   startDate  {Date}              – Start of the displayed range.
 *   endDate    {Date}              – End of the displayed range.
 *   onPrev     {() => void}        – Called when the left arrow is clicked.
 *   onNext     {() => void}        – Called when the right arrow is clicked.
 *   disabled   {boolean}           – Disables all interaction. Defaults to false.
 *   minDate    {Date}              – Optional lower boundary; prev button is disabled when startDate <= minDate.
 *   maxDate    {Date}              – Optional upper boundary; next button is disabled when endDate >= maxDate.
 */
function TimeRangeScroll({
  startDate,
  endDate,
  onPrev,
  onNext,
  disabled = false,
  minDate,
  maxDate,
}) {
  const classes = useStyles();

  const prevDisabled = disabled || (minDate instanceof Date && startDate instanceof Date && startDate <= minDate);
  const nextDisabled = disabled || (maxDate instanceof Date && endDate instanceof Date && endDate >= maxDate);

  return (
    <div className={clsx(classes.container, { [classes.containerDisabled]: disabled })}>
      <button
        className={classes.arrowButton}
        onClick={onPrev}
        disabled={prevDisabled}
        aria-label="Previous period"
      >
        <ChevronLeft size={20} strokeWidth={1.75} />
      </button>

      <span className={classes.dateRange}>
        {formatDate(startDate)}
        <ArrowRight size={12} strokeWidth={1.75} className={classes.arrowIcon} />
        {formatDate(endDate)}
      </span>

      <button
        className={classes.arrowButton}
        onClick={onNext}
        disabled={nextDisabled}
        aria-label="Next period"
      >
        <ChevronRight size={20} strokeWidth={1.75} />
      </button>
    </div>
  );
}

export default TimeRangeScroll;

const useStyles = makeStyles(() => ({
  container: {
    height: "48px",
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    borderRadius: "12px",
    padding: "4px",
    boxSizing: "border-box",
  },
  containerDisabled: {
    opacity: 0.5,
  },
  arrowButton: {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F8F8F8",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#0C0E29",
    transition: "background-color 0.15s ease",
    lineHeight: 0,

    "&:hover:not(:disabled)": {
      backgroundColor: "#EDEDED",
    },
    "&:disabled": {
      cursor: "not-allowed",
    },
  },
  dateRange: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "0 24px",
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    fontWeight: "normal",
    color: "#0C0E29",
    whiteSpace: "nowrap",
  },
  arrowIcon: {
    color: "#0C0E29",
    flexShrink: 0,
  },
}));
